/**
 * VCheckWpForm — Verbau-Checkliste Wärmepumpe (V-Check WP) Container.
 *
 * Iter88zv (2026-02-19) — File-Split: Field/Photo/Signature/Section liegen
 * jetzt in ./vcheck/*, dieser Container kümmert sich um Datenladen,
 * Autosave, Fortschritt, PDF, Finalize.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Save, FileCheck, Download, Send } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import VCheckSection from "./vcheck/VCheckSection";
import VCheckSignatureBlock from "./vcheck/VCheckSignatureBlock";
import VCheckWpWizard from "./VCheckWpWizard";
import EmailApprovalDialog from "@/components/EmailApprovalDialog";
import { useVCheckOffline, isOfflineError } from "@/lib/vcheckOffline";
import { useVCheckLive } from "@/lib/useVCheckLive";

export default function VCheckWpForm({ projectId, onFinalized }) {
  const [schema, setSchema] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Iter89z-wiz (2026-02-26) — Wizard als Standard, pro Session persistent
  // (sessionStorage statt localStorage, damit nicht permanent gespeichert).
  const [wizardMode, setWizardMode] = useState(() => {
    try {
      const v = sessionStorage.getItem("vcheck_wp_view");
      return v !== "classic";
    } catch { return true; }
  });
  const toggleView = useCallback(() => {
    setWizardMode((prev) => {
      try { sessionStorage.setItem("vcheck_wp_view", prev ? "classic" : "wizard"); } catch {}
      return !prev;
    });
  }, []);
  const dirtyRef = useRef({});
  const dirtyTsRef = useRef({}); // Iter89z-wiz3: pro Dirty-Key Zeitstempel merken
  const ownTsRef = useRef({}); // Server-Zeitstempel eigener gespeicherter Änderungen (sonst Konflikt mit sich selbst)
  const timerRef = useRef(null);

  // Iter89z-off (2026-02-26) — Offline-Handler für Patches, Fotos und Snapshots
  const offline = useVCheckOffline(projectId, { onSynced: () => reload() });

  // Iter89z-wiz3 (2026-02-26) — WebSocket Live-Presence + Remote-Change-Notify
  const live = useVCheckLive(projectId);
  // Konflikt-Modal — gefüllt vom Patch-Response wenn `conflicts: [...]` zurückkam
  const [conflicts, setConflicts] = useState([]);
  // Auto-reload bei Remote-Edit (anderer Rep hat etwas geändert)
  useEffect(() => {
    if (!live.lastRemoteChange) return;
    const msg = live.lastRemoteChange;
    toast.info(`${msg.by_name || "Kollege"} hat „${msg.key}" geändert`,
               { duration: 2500 });
    // kleines Debounce, falls mehrere Nachrichten in Folge kommen
    const t = setTimeout(() => reload(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.lastRemoteChange]);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/vcheck-wp`);
      setDoc(r.data);
      offline.cacheServerSnapshot(r.data);
    } catch (e) {
      if (isOfflineError(e)) {
        // Fallback auf lokalen Snapshot — damit das Formular auch offline
        // vollständig funktioniert, falls das Projekt schon einmal online
        // geöffnet war.
        const cached = await offline.loadCachedSnapshot();
        if (cached) {
          setDoc(cached);
          toast.info("Offline — gecachte Version geladen");
        }
      } else {
        throw e;
      }
    }
  }, [projectId, offline]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [sch, docR] = await Promise.all([
          api.get(`/projects/${projectId}/vcheck-wp/schema`),
          api.get(`/projects/${projectId}/vcheck-wp`),
        ]);
        if (!alive) return;
        setSchema(sch.data.schema);
        setDoc(docR.data);
        // Server-Snapshot cachen für Offline-Erst-Öffnung
        offline.cacheServerSnapshot(docR.data);
      } catch (e) {
        if (isOfflineError(e)) {
          const cached = await offline.loadCachedSnapshot();
          if (cached) {
            setDoc(cached);
            // Minimales Schema aus dem Cache — notfalls Dummy mit Schema aus Snapshot
            toast.info("Offline — gecachte V-Check-Version geladen");
          } else {
            toast.error("Offline und keine gecachte Version vorhanden");
          }
        } else {
          toast.error("V-Check konnte nicht geladen werden");
        }
      } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const flush = useCallback(async () => {
    const payload = { ...dirtyRef.current };
    const expected_ts = { ...dirtyTsRef.current };
    if (Object.keys(payload).length === 0) return;
    dirtyRef.current = {};
    dirtyTsRef.current = {};
    setSaving(true);
    try {
      const r = await api.patch(`/projects/${projectId}/vcheck-wp`, {
        data: payload,
        expected_ts,
      });
      // Konflikt-Detect (Iter89z-wiz3): wenn der Server neuere Werte hat,
      // zeigen wir die Diff-UI
      for (const k of r.data?.updated_keys || []) ownTsRef.current[k] = r.data.ts;
      if (Array.isArray(r.data?.conflicts) && r.data.conflicts.length > 0) {
        setConflicts((prev) => [...prev, ...r.data.conflicts]);
      }
    } catch (e) {
      if (isOfflineError(e)) {
        await offline.enqueuePatch({ data: payload, expected_ts });
        toast.info("Offline — Änderungen lokal gespeichert", { duration: 2000 });
      } else {
        dirtyRef.current = { ...payload, ...dirtyRef.current };
        dirtyTsRef.current = { ...expected_ts, ...dirtyTsRef.current };
        toast.error("Autosave fehlgeschlagen");
      }
    } finally { setSaving(false); }
  }, [projectId, offline]);

  const onFieldChange = (key, value) => {
    setDoc((prev) => ({ ...prev, data: { ...(prev?.data || {}), [key]: value } }));
    dirtyRef.current[key] = value;
    // Zeitpunkt des Server-Werts speichern (beim ersten Dirty-Mark), damit
    // der Server nicht denkt, wir überschreiben ohne Kenntnis des aktuellen
    // Stands.
    if (!dirtyTsRef.current[key]) {
      const a = ownTsRef.current[key];
      const b = doc?.field_ts?.[key];
      dirtyTsRef.current[key] = (a && b ? (a > b ? a : b) : a || b) || null;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 800);
  };
  useEffect(() => () => { if (timerRef.current) { clearTimeout(timerRef.current); flush(); } }, [flush]);

  // KI-Vorschläge übernehmen / verwerfen (remarks=true → KI-Hinweise)
  const onAiDecide = async (keys, accept, remarks = false) => {
    if (timerRef.current) { clearTimeout(timerRef.current); await flush(); }
    try {
      const r = await api.post(`/projects/${projectId}/vcheck-wp/ai/decide`, { keys, accept, remarks });
      for (const k of r.data?.applied || []) ownTsRef.current[k] = r.data.ts;
      if (accept && r.data?.applied?.length) toast.success(`${r.data.applied.length} KI-Wert${r.data.applied.length === 1 ? "" : "e"} übernommen`);
      if (remarks && accept) toast.success("KI-Hinweise in „Offene Punkte“ übernommen");
      await reload();
    } catch (e) {
      toast.error("KI-Vorschlag konnte nicht gespeichert werden");
    }
  };

  // KI-Gesamtauswertung starten und bis zum Ergebnis abfragen
  const [aiRunning, setAiRunning] = useState(false);
  const pollAi = useCallback(async () => {
    for (let i = 0; i < 60; i += 1) {
      await new Promise((res) => setTimeout(res, 4000));
      try {
        const r = await api.get(`/projects/${projectId}/vcheck-wp`);
        if (r.data?.ai_job?.status !== "running") {
          setDoc(r.data);
          setAiRunning(false);
          if (r.data?.ai_job?.status === "done") toast.success(`KI fertig · ${r.data.ai_job.count || 0} Vorschläge`);
          return;
        }
      } catch { /* weiter versuchen */ }
    }
    setAiRunning(false);
  }, [projectId]);
  const onRunAiFull = async () => {
    setAiRunning(true);
    try {
      await api.post(`/projects/${projectId}/vcheck-wp/ai/full`);
      pollAi();
    } catch (e) {
      setAiRunning(false);
      toast.error(e.response?.data?.detail || "KI-Auswertung konnte nicht gestartet werden");
    }
  };
  useEffect(() => {
    if (doc?.ai_job?.status === "running" && !aiRunning) { setAiRunning(true); pollAi(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.ai_job?.status]);

  // „Entfällt / nicht vorhanden“ setzen (reason = Text) oder zurücknehmen (reason = null)
  const onNaChange = async (key, reason) => {
    setDoc((prev) => {
      const na = { ...(prev?.na || {}) };
      if (reason === null) delete na[key]; else na[key] = { reason };
      return { ...prev, na };
    });
    const patch = { data: {}, na: { [key]: reason } };
    try {
      await api.patch(`/projects/${projectId}/vcheck-wp`, patch);
    } catch (e) {
      if (isOfflineError(e)) await offline.enqueuePatch(patch);
      else toast.error("„Entfällt“ konnte nicht gespeichert werden");
    }
  };

  const finalize = async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); await flush(); }
    setFinalizing(true);
    try {
      const r = await api.post(`/projects/${projectId}/vcheck-wp/finalize`);
      if (!r.data?.ok) {
        toast.warning(`Noch ${r.data.missing_fields?.length || 0} Pflichtfelder + ${r.data.missing_photos?.length || 0} Foto-Blöcke offen`);
        await reload();
      } else {
        toast.success(`V-Check WP finalisiert · Projekt-Status auf „Vor-Ort-Aufnahme abgeschlossen" gesetzt`);
        await reload();
        onFinalized?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Finalisierung fehlgeschlagen");
    } finally { setFinalizing(false); }
  };

  // Iter88zv Phase 3 — PDF-Vordruck 1:1 erzeugen & anzeigen / herunterladen
  const openPdf = async (asDownload = false) => {
    if (timerRef.current) { clearTimeout(timerRef.current); await flush(); }
    setPdfLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/vcheck-wp/pdf`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (asDownload) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `VCheck_WP_${projectId.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("PDF wird heruntergeladen");
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("PDF konnte nicht erzeugt werden");
    } finally { setPdfLoading(false); }
  };

  // Iter88zw (2026-02-19) — V-Check an Installation weiterleiten (E-Mail).
  const [sendDialog, setSendDialog] = useState(false);
  const [installerInfo, setInstallerInfo] = useState(null);
  const openSendDialog = async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); await flush(); }
    try {
      const p = await api.get(`/projects/${projectId}`);
      const inst = p.data?.project?.installer_id
        ? (await api.get(`/installers`)).data?.find?.((x) => x.id === p.data.project.installer_id)
        : null;
      setInstallerInfo({
        recipient: inst?.email || "",
        name: inst?.name || "(keine Installation zugewiesen)",
        projectNumber: p.data?.project?.project_number || "—",
      });
      setSendDialog(true);
    } catch (e) {
      toast.error("Installations-Daten konnten nicht geladen werden");
    }
  };
  const doSend = async (cc) => {
    try {
      const r = await api.post(`/projects/${projectId}/vcheck-wp/send-to-installer`, {
        recipient_email: installerInfo?.recipient, cc,
      });
      if (r.data?.email_error) {
        toast.warning(`Versand fehlgeschlagen: ${r.data.email_error}`);
      } else {
        const cc_line = (Array.isArray(cc) && cc.length) ? ` · CC: ${cc.join(", ")}` : "";
        toast.success(`✓ V-Check an ${r.data?.installer_name || r.data?.recipient_email}${cc_line} versendet`,
                      { duration: 8000 });
      }
      setSendDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };

  const progress = doc?.progress;
  const totalPct = useMemo(() => {
    if (!progress) return 0;
    const num = progress.required_done + progress.photo_blocks_done + (progress.signed ? 1 : 0);
    const den = progress.required_total + progress.photo_blocks_total + 1;
    return den ? Math.round((num / den) * 100) : 0;
  }, [progress]);

  if (loading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2" />V-Check wird geladen…</div>;
  if (!schema || !doc) return <div className="p-8 text-red-400">Fehler beim Laden.</div>;

  const sigField = { label: "Unterschrift Kunde",
    hint: "Digital auf Tablet/Handy signieren oder Foto der Papier-Unterschrift hochladen" };

  return (
    <div className="space-y-3" data-testid="vcheck-wp-form">
      {!wizardMode && (
      <div className="xl:sticky xl:top-[125px] z-10 bg-[#09090B] py-3 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <div className="text-[14px] font-bold text-zinc-100 flex items-center gap-2">
              <FileCheck size={16} className="text-emerald-400" />
              Verbau-Checkliste Wärmepumpe
              {doc.finalized_at && (
                <span className="text-[10.5px] px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/40 rounded text-emerald-300">
                  ✓ Finalisiert
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {progress?.required_done}/{progress?.required_total} Pflichtfelder · {progress?.photo_blocks_done}/{progress?.photo_blocks_total} Foto-Blöcke · Unterschrift {progress?.signed ? "✓" : "○"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {saving && <span className="text-[10.5px] text-zinc-500 inline-flex items-center gap-1"><Save size={11} className="animate-pulse" />Speichere…</span>}
            <button onClick={() => openPdf(false)} disabled={pdfLoading}
                    className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="vcheck-pdf-btn"
                    title="PDF in neuem Tab anzeigen">
              {pdfLoading ? <Loader2 size={12} className="animate-spin" /> : <FileCheck size={12} />}
              Anzeigen
            </button>
            <button onClick={() => openPdf(true)} disabled={pdfLoading}
                    className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="vcheck-pdf-download-btn"
                    title="PDF direkt herunterladen">
              <Download size={12} />
              Download
            </button>
            <button onClick={openSendDialog}
                    className="h-9 px-3 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-300 text-[12.5px] font-semibold inline-flex items-center gap-1.5"
                    data-testid="vcheck-send-installer-btn"
                    title="V-Check-PDF an Installation weiterleiten">
              <Send size={12} />
              An Installation
            </button>
            <button onClick={finalize} disabled={finalizing || doc.finalized_at}
                    className="h-9 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="vcheck-finalize-btn">
              {finalizing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              Technisch freigeben
            </button>
          </div>
        </div>
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div className={`h-full transition-all ${totalPct === 100 ? "bg-emerald-500" : "bg-emerald-500/70"}`} style={{ width: `${totalPct}%` }} />
        </div>
      </div>
      )}

      {wizardMode ? (
        <VCheckWpWizard
          schema={schema}
          doc={doc}
          projectId={projectId}
          onFieldChange={onFieldChange}
          onNaChange={onNaChange}
          onAiDecide={onAiDecide}
          onRunAiFull={onRunAiFull}
          aiRunning={aiRunning}
          onReload={reload}
          onFinalize={finalize}
          onToggleClassicView={toggleView}
          saving={saving}
          finalizing={finalizing}
          onOpenPdf={openPdf}
          onOpenSend={openSendDialog}
          pdfLoading={pdfLoading}
          offline={offline}
          live={live}
          conflicts={conflicts}
          onResolveConflict={(key, keepServer) => {
            setConflicts((prev) => prev.filter((c) => c.key !== key));
            if (!keepServer) {
              // Nutzer will seinen eigenen Wert durchsetzen → nochmal ohne
              // expected_ts versuchen (Server akzeptiert dann die Überschreibung)
              const c = conflicts.find((cc) => cc.key === key);
              if (c) {
                api.patch(`/projects/${projectId}/vcheck-wp`,
                          { data: { [key]: c.client_value } })
                   .then(() => reload())
                   .catch(() => toast.error("Übernahme fehlgeschlagen"));
              }
            } else {
              reload();
            }
          }}
        />
      ) : (
        <>
          <div className="flex items-center justify-end mb-2">
            <button
              onClick={toggleView}
              data-testid="vcheck-toggle-wizard"
              className="h-9 px-3 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 text-[12px] font-semibold inline-flex items-center gap-1.5"
              title="Geführten Modus wieder einschalten"
            >
              <FileCheck size={13} /> Geführter Modus
            </button>
          </div>
          {schema.map((sec, i) => (
            <VCheckSection key={sec.section} section={sec} data={doc.data || {}}
                           photos={doc.photos || {}} projectId={projectId}
                           onChange={onFieldChange} onPhotosChanged={reload}
                           missingKeys={progress?.missing_fields || []}
                           missingPhotos={progress?.missing_photos || []}
                           defaultOpen={i < 2} />
          ))}

          <VCheckSignatureBlock field={sigField} doc={doc} projectId={projectId} onChanged={reload} />
        </>
      )}

      {/* Iter88zw — V-Check an Installation weiterleiten (Approval-Modal + CC + PDF-Preview) */}
      <EmailApprovalDialog
        open={sendDialog}
        onClose={() => setSendDialog(false)}
        onConfirm={doSend}
        recipient={installerInfo?.recipient}
        subject={`V-Check Wärmepumpe · Projekt ${installerInfo?.projectNumber || ""} · NOVATUS Energie`}
        attachments={[{ name: `VCheck_WP_${installerInfo?.projectNumber || ""}.pdf` }]}
        bodyPreview={
          `Sehr geehrte Damen und Herren,\n\n` +
          `anbei erhalten Sie die vollständige Verbau-Checkliste (V-Check) ` +
          `zum Projekt ${installerInfo?.projectNumber || ""}.\n\n` +
          `Bitte prüfen Sie die technischen Angaben, Rohrdimensionen und ` +
          `Foto-Dokumentation vor Terminierung der Montage.\n\n` +
          `Mit freundlichen Grüßen\nNOVATUS Energie GmbH`
        }
        defaultCc={[]}
        testIdPrefix="vcheck-send"
        pdfFetcher={sendDialog ? (async () => {
          const r = await api.post(`/projects/${projectId}/vcheck-wp/pdf`, {}, { responseType: "blob" });
          return r.data;
        }) : null}
      />
    </div>
  );
}
